// Vendor Imports
import _ from 'lodash';
import React, { Component } from 'react';

// Project Imports
import { FilterFooter } from '../FilterFooter';
import FilterHeader from '../FilterHeader';

import {
  ForgeCheckbox,
  ForgeDivider,
  ForgeDrawer,
  ForgeIcon,
  ForgeList,
  ForgeListItem,
  ForgeSelect,
  ForgeTextField,
} from '@tylertech/forge-react';
import { PicklistOption } from 'common/components/Picklist';
import { I18n } from 'common/i18n';
import { FilterValue, OPERATOR } from '../SoqlFilter';
import { FilterColumnsMap, FilterEditorProps } from '../types';
import { addPopupListener } from './InputFocus';
import * as TextFilter from '../lib/Filters/TextFilter';
import * as BaseFilter from '../lib/Filters/BaseFilter';
import { getOperator, isEqualityFilter } from '../lib/Filters/index';

// Constants
const scope = 'shared.components.filter_bar.text_filter';
export const DEFAULT_FETCH_RESULTS_DEBOUNCE_WAIT_TIME = 400;
export const SHOW_RESULTS_COUNT = 20;
export const FILTER_OFFSET = { DEFAULT: 0, MAX: 20 };
const INFINITE_SCROLL_DATA_LOAD_OFFSET = 25;

export interface TextFilterEditorProps extends FilterEditorProps {
  appliedFilter: TextFilter.TextSoqlFilter;
  columns: FilterColumnsMap;
}
interface TextFilterEditorState {
  /**
   * NOTE: Other filter editors store individual UI components' values in state
   * and then apply them to the config separately. Text filter applies the values
   * directly to the filter config, so we store the entire dirty filter in state to
   * keep rerenders the same.
   */
  dirtyFilter: TextFilter.TextSoqlFilter;
  isLoadingData: boolean;
  topXOptions: PicklistOption[];
  picklistOffset: number;
}

export class TextFilterEditor extends Component<TextFilterEditorProps, TextFilterEditorState> {
  textFilter: HTMLDivElement;
  picklistContainerRef = React.createRef<HTMLDivElement>();
  removePopupListener = () => {};
  popupListenerRemoved = false;

  constructor(props: TextFilterEditorProps) {
    super(props);

    const { appliedFilter } = props;

    const picklistOffset = FILTER_OFFSET.DEFAULT;

    this.state = {
      dirtyFilter: _.cloneDeep(appliedFilter),
      isLoadingData: true,
      topXOptions: [],
      picklistOffset,
    };

    _.bindAll(this, [
      'applyFilter',
      'getSuggestions',
      'getAllSuggestions',
      'isDirty',
      'onSelectOption',
      'renderHeader',
      'renderLoadingSpinner',
      'renderSearchTextbox',
      'resetFilter',
      'updateSelectedValues',
      'onFilterList',
    ]);
  }

  getDataProvider(props: FilterEditorProps) {
    return props.dataProvider[0];
  }

  componentDidMount() {
    this.getTopXOptionsRespectingFilters();
    const { popupRef } = this.props;
    this.removePopupListener = addPopupListener(popupRef, this.textFilter, () => {
      this.popupListenerRemoved = true;
    });
  }

  componentWillUnmount() {
    const { popupRef } = this.props;
    if (!this.popupListenerRemoved) {
      popupRef?.current?.removeEventListener('forge-popover-position', this.removePopupListener);
    }
  }

  UNSAFE_componentWillUpdate(nextProps: Readonly<TextFilterEditorProps>): void {
    const { appliedFilter } = this.props;

    if (!_.isEqual(appliedFilter, nextProps.appliedFilter)) {
      this.setState({ dirtyFilter: _.cloneDeep(nextProps.appliedFilter) });
    }
  }

  handleScrollForPicklist = _.debounce(() => {
    const picklistContainerScrollTop = this.picklistContainerRef?.current?.scrollTop ?? 0;
    const picklistContainerHeight = this.picklistContainerRef?.current?.clientHeight ?? 0;
    const picklistContainerScrollHeight = this.picklistContainerRef?.current?.scrollHeight ?? 0;
    const picklistContainerBottomPosition = picklistContainerScrollTop + picklistContainerHeight;

    if (picklistContainerBottomPosition + INFINITE_SCROLL_DATA_LOAD_OFFSET > picklistContainerScrollHeight) {
      this.getTopXOptionsRespectingFilters();
    }
  }, DEFAULT_FETCH_RESULTS_DEBOUNCE_WAIT_TIME);

  /**
   * Top X options are set up once and placed into state. This gets the Top X items
   *  and inserts the (No Value) item at the top of the list.
   */
  getTopXOptionsRespectingFilters = () => {
    const { allFilters, allParameters, appliedFilter, dataProvider, columns, independentTextFilters } =
      this.props;
    const { dirtyFilter, picklistOffset, topXOptions } = this.state;

    this.setState({ isLoadingData: true });
    const filter = appliedFilter;
    const infiniteScroll = appliedFilter.columns.length === 1;

    TextFilter.getTopXOptionsRespectingFilters({
      filter,
      allFilters,
      allParameters,
      offset: infiniteScroll ? picklistOffset : 0,
      dataProviderConfigs: dataProvider,
      independentFilters: independentTextFilters,
    })
      .then((result) => {
        // Check to make sure component is mounted
        if (!this.textFilter) {
          return;
        }

        const newTopXOptions: PicklistOption[] = (result.topXOptions ?? []).map((option) => {
          return {
            title: option.value,
            value: option.value,
          };
        });

        if (!infiniteScroll || (infiniteScroll && picklistOffset === 0)) {
          newTopXOptions.unshift(this.getNullOption());
        }
        if (infiniteScroll && picklistOffset > 0) {
          newTopXOptions.unshift(...topXOptions);
        }

        const newPicklistOffset = picklistOffset + SHOW_RESULTS_COUNT;
        this.setState({
          isLoadingData: false,
          topXOptions: newTopXOptions,
          picklistOffset: newPicklistOffset,
        });
      })
      .catch((error) => {
        this.setState({ isLoadingData: false });
        console.error(`Soql like top values failed for ${BaseFilter.getFilterName(dirtyFilter, columns)}:`);
        console.error(error);
      });
  };

  getNullOption = () => {
    // Create the "null" suggestion to allow filtering on empty values.
    return {
      title: I18n.t('no_value', { scope }),
      value: null,
    };
  };

  getSuggestions(searchTerm: string) {
    const { allFilters, allParameters, dataProvider, columns } = this.props;
    const { dirtyFilter } = this.state;

    if (_.isEmpty(searchTerm)) {
      return Promise.resolve({ results: [] });
    }

    const filter = dirtyFilter;
    return TextFilter.getSuggestions({
      filter,
      searchTerm,
      allFilters,
      allParameters,
      dataProviderConfigs: dataProvider,
    }).catch((error) => {
      console.error(`Soql like search failed for ${BaseFilter.getFilterName(dirtyFilter, columns)}:`);
      console.error(error);
    });
  }

  onSelectOption(option?: PicklistOption | null, checkedState: boolean = true) {
    const { dirtyFilter } = this.state;

    if (dirtyFilter.singleSelect) {
      this.updateSelectedValues([option?.value], this.applyFilter);
    } else if (checkedState) {
      this.updateSelectedValues(_.union(TextFilter.getOperands(dirtyFilter), [option?.value]));
    } else {
      this.updateSelectedValues(_.without(TextFilter.getOperands(dirtyFilter), option?.value));
    }
  }

  updateSelectedValues(nextSelectedValues: FilterValue[], callback?: () => void) {
    const { dirtyFilter } = this.state;
    const updatedFilter = TextFilter.getEqualityTextFilter(dirtyFilter, {
      values: _.uniq(nextSelectedValues),
    });
    this.setState({ dirtyFilter: _.cloneDeep(updatedFilter) }, callback);
  }

  resetFilter() {
    const { columns, onUpdate } = this.props;
    const { dirtyFilter } = this.state;

    const resetFilter = BaseFilter.reset(dirtyFilter, columns);
    onUpdate(resetFilter);
  }

  applyFilter() {
    const { onUpdate } = this.props;
    const { dirtyFilter } = this.state;

    onUpdate(dirtyFilter);
  }

  isDirty() {
    const { dirtyFilter } = this.state;
    const { appliedFilter } = this.props;

    return TextFilter.hasOperands(dirtyFilter) && !_.isEqual(dirtyFilter, appliedFilter);
  }

  renderHeader() {
    const { dirtyFilter } = this.state;
    const { columns } = this.props;
    const headerProps = {
      name: BaseFilter.getFilterName(dirtyFilter, columns),
    };

    const dropdownProps = {
      onChange: (event: CustomEvent) => {
        const updatedFilter: TextFilter.TextSoqlFilter = TextFilter.setOperator(dirtyFilter, event.detail);
        this.setState({ dirtyFilter: updatedFilter });
      },
      options: [
        { label: I18n.t('is', { scope }), value: OPERATOR.EQUALS },
        { label: I18n.t('is_not', { scope }), value: OPERATOR.NOT_EQUAL },
        { label: I18n.t('starts_with', { scope }), value: OPERATOR.STARTS_WITH },
        { label: I18n.t('contains', { scope }), value: OPERATOR.CONTAINS },
        { label: I18n.t('does_not_contain', { scope }), value: OPERATOR.DOES_NOT_CONTAIN },
      ],
      value: getOperator(dirtyFilter),
      variant: 'plain',
      theme: 'primary',
      density: 'small',
      popoverIcon: false,
      className: 'filter-operator-dropdown',
    };

    const dropdown = !_.get(dirtyFilter, 'singleSelect', false) ? (
      <ForgeSelect {...dropdownProps}>
        <ForgeIcon slot="end" name="chevron_down" theme="primary" icon_type="button" />
      </ForgeSelect>
    ) : null;

    return <FilterHeader {...headerProps}>{dropdown}</FilterHeader>;
  }

  async getAllSuggestions(filterText: string) {
    const options = await this.getSuggestions(filterText);
    const newOptions: PicklistOption[] = (options?.results ?? []).map((option) => {
      return {
        title: option.title,
        value: option.title,
      };
    });
    this.setState({ topXOptions: newOptions });
  }

  onFilterList(filterText: string) {
    if (_.isEmpty(filterText)) {
      return;
    }
    this.getAllSuggestions(filterText);
  }

  renderSearchTextbox() {
    return (
      <ForgeTextField variant="filled" density="small">
        <ForgeIcon name="search" slot="start" />
        <label htmlFor="search-text-filter-input">Search values</label>
        <input
          id="search-text-filter-input"
          type="text"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.onFilterList(event.target.value)}
        ></input>
      </ForgeTextField>
    );
  }

  renderLoadingSpinner() {
    return (
      <div className="loading-spinner-container">
        <span className="spinner-default" />
      </div>
    );
  }

  renderPicklist() {
    const { dirtyFilter, topXOptions } = this.state;
    if (topXOptions.length === 0) {
      return;
    }

    const selectedValues = TextFilter.getOperands(dirtyFilter);
    const options = _.filter(topXOptions, (option) => !_.includes(selectedValues, option.value));

    const selectedOptions = _.map(selectedValues, (selectedValue) => ({
      title: _.isNull(selectedValue) ? I18n.t('no_value', { scope }) : selectedValue,
      value: selectedValue,
    }));

    const selectedListItems =
      !dirtyFilter.singleSelect && selectedOptions.length > 0
        ? selectedOptions.map((option) => (
            <ForgeListItem key={`selected-option-${option.title}`} value={option.value}>
              <ForgeCheckbox
                slot="start"
                checked={selectedValues.includes(option.value)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  this.onSelectOption(option, event.target.checked)
                }
                data-testid={`${option.title}-selected-text-filter-option`}
              ></ForgeCheckbox>
              <span>{option.title}</span>
            </ForgeListItem>
          ))
        : null;

    const listItems = options.map((option) => (
      <ForgeListItem key={`option-${option.title}`} value={option.value}>
        <ForgeCheckbox
          slot="start"
          checked={selectedValues.includes(option.value)}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            this.onSelectOption(option, event.target.checked)
          }
          data-testid={`${option.title}-text-filter-option`}
        ></ForgeCheckbox>
        <span>{option.title}</span>
      </ForgeListItem>
    ));

    const divider = <ForgeDivider key="forge-divider"></ForgeDivider>;
    const withDivider = selectedListItems?.concat(divider);
    const allListItems = withDivider?.concat(listItems) ?? listItems;

    return (
      <div className="text-filter-list-container">
        <ForgeDivider></ForgeDivider>
        <ForgeDrawer open onScroll={this.handleScrollForPicklist} ref={this.picklistContainerRef}>
          <ForgeList className="forge-typography--body2" dense role="listbox">
            {allListItems}
          </ForgeList>
        </ForgeDrawer>
        <ForgeDivider></ForgeDivider>
      </div>
    );
  }

  renderTextBox() {
    const containsValue = TextFilter.getOperands(this.state.dirtyFilter)[0] ?? '';
    const inputProps = {
      className: 'text-input',
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        const { dirtyFilter } = this.state;
        const updatedFilter = TextFilter.getContainsTextFilter(dirtyFilter, {
          operand: event.target?.value ?? '',
        });
        this.setState({ dirtyFilter: _.cloneDeep(updatedFilter) });
      },
      value: containsValue as string,
    };

    return (
      <ForgeTextField variant="filled" density="small">
        <input {...inputProps} />
      </ForgeTextField>
    );
  }

  render() {
    const { isReadOnly, onRemove, showRemoveButtonInFooter } = this.props;
    const { dirtyFilter } = this.state;

    const footerProps = {
      disableApplyFilter: !this.isDirty(),
      isDrilldown: dirtyFilter.isDrilldown,
      isReadOnly,
      onClickApply: () => this.applyFilter(),
      onClickRemove: onRemove,
      onClickReset: this.resetFilter,
      showApplyButton: !dirtyFilter.singleSelect,
      showRemoveButton: showRemoveButtonInFooter,
    };

    let suggestionsAutocomplete;
    let picklist;
    let textBox;

    if (isEqualityFilter(dirtyFilter)) {
      suggestionsAutocomplete = this.renderSearchTextbox();
      picklist = this.renderPicklist();
    } else {
      suggestionsAutocomplete = null;
      picklist = null;
      textBox = this.renderTextBox();
    }

    return (
      <div className="filter-controls text-filter" ref={(el: HTMLDivElement) => (this.textFilter = el)}>
        <div className="column-container">
          {this.renderHeader()}
          {suggestionsAutocomplete}
          {picklist}
          {textBox}
        </div>
        <FilterFooter {...footerProps} />
      </div>
    );
  }
}
